import { fetchWithAuth, getCore, postCore, patchCore } from "../../utils/httpInterceptor";

const coreBaseUrl =
  process.env.REACT_APP_CORE_BACKEND_URL || process.env.REACT_APP_LOAD_PAGE_URL;

const parseSseBlock = (block) => {
  let eventName = "message";
  const dataLines = [];
  block.split(/\r?\n/).forEach((line) => {
    if (line.startsWith("event:")) {
      eventName = line.substring(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.substring(5).trimStart());
    }
  });
  if (!dataLines.length) {
    return null;
  }
  const data = dataLines.join("\n");
  try {
    return { eventName, payload: JSON.parse(data) };
  } catch {
    return { eventName, payload: data };
  }
};

const consumeSseStream = async (stream, onEvent, signal) => {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (!signal?.aborted) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";
    blocks.forEach((block) => {
      const parsed = parseSseBlock(block);
      if (parsed) onEvent(parsed.payload, parsed.eventName);
    });
  }
};

class MessagingService {
  static async mailbox({ type = "E", search = "", page = 0, size = 25 } = {}) {
    const params = new URLSearchParams({
      type,
      search,
      page: String(page),
      size: String(size),
    });
    return getCore(`/api/core/messages?${params.toString()}`);
  }

  static async detail(messageId, includeAttachmentContent = false) {
    return getCore(`/api/core/messages/${messageId}?includeAttachmentContent=${includeAttachmentContent}`);
  }

  static async create(payload) {
    return postCore("/api/core/messages", payload);
  }

  static async updateEntry(entryId, payload) {
    return patchCore(`/api/core/messages/entries/${entryId}`, payload);
  }

  static async markRead(entryId) {
    return patchCore(`/api/core/messages/entries/${entryId}/read`, {});
  }

  static async counts() {
    return getCore("/api/core/messages/counts");
  }

  static async users() {
    return getCore("/api/core/messages/users");
  }

  static async groups() {
    return getCore("/api/core/messages/groups");
  }

  static async createGroup(payload) {
    return postCore("/api/core/messages/groups", payload);
  }

  static attachmentUrl(attachmentId) {
    return `${coreBaseUrl}/api/core/messages/attachments/${attachmentId}`;
  }

  static async downloadAttachment(attachment) {
    const response = await fetchWithAuth(`/api/core/messages/attachments/${attachment.id}`, {
      baseUrl: coreBaseUrl,
      method: "GET",
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.fileName || "attachment";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  static subscribe(onEvent, onError) {
    const controller = new AbortController();
    let stopped = false;
    let retryDelayMs = 1000;
    const maxRetryDelayMs = 30000;

    const connect = () => {
      if (stopped) return;

      // Uses fetchWithAuth (not a raw fetch) so this stream shares the same token
      // storage lookup and 401-refresh logic as every other request in the app.
      // Confirmed in production (2026-09-18): a raw fetch() here read its own,
      // separate copy of the token and never refreshed it on a 401, so once that
      // token went stale the stream reconnected every ~30s (the backoff ceiling)
      // forever, 401-ing indefinitely, while the rest of the session - going
      // through fetchWithAuth - kept refreshing normally and working fine.
      fetchWithAuth("/api/core/messages/stream", {
        baseUrl: coreBaseUrl,
        method: "GET",
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok || !response.body) {
            throw new Error(`SSE connection failed: ${response.status}`);
          }
          retryDelayMs = 1000;
          return consumeSseStream(response.body, onEvent, controller.signal);
        })
        .then(() => {
          // Stream ended (server closed it, or an intermediary dropped the connection):
          // reconnect instead of leaving the client silently stuck without live updates.
          scheduleReconnect();
        })
        .catch((error) => {
          if (controller.signal.aborted || stopped) return;
          if (error?.isAuthError) {
            // fetchWithAuth already tried refreshing the token and it failed -
            // the session is genuinely dead (it also triggers the app's normal
            // logout/redirect). Reconnecting with the same dead token forever
            // is exactly the bug above: stop instead of scheduling another retry.
            stopped = true;
            if (onError) onError(error);
            return;
          }
          if (onError) onError(error);
          scheduleReconnect();
        });
    };

    const scheduleReconnect = () => {
      if (stopped || controller.signal.aborted) return;
      const delay = retryDelayMs;
      retryDelayMs = Math.min(retryDelayMs * 2, maxRetryDelayMs);
      setTimeout(() => {
        if (!stopped && !controller.signal.aborted) connect();
      }, delay);
    };

    connect();

    return () => {
      stopped = true;
      controller.abort();
    };
  }
}

export default MessagingService;
